import { AlertTriangle, Eye, EyeOff, PhoneCall } from "lucide-react";
import * as React from "react";
import { useForm } from "react-hook-form";
import { Link, Navigate, useLocation } from "react-router-dom";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { ApiError, api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { zodResolver } from "@/lib/form";
import { postLoginPath } from "@/lib/nav";

const schema = z.object({
  email: z.string().min(1, "Enter your email address").email("That does not look like an email"),
  password: z.string().min(1, "Enter your password"),
});
type FormValues = z.infer<typeof schema>;

/**
 * Whether this server can email a reset link (ADR-057 in `platform`), which decides whether the
 * footer offers one or keeps the old "ask an admin" line.
 *
 * Asked once per mount, outside react-query: this page has no session and therefore no query
 * client of its own. A server that does not answer is treated as one that cannot send — the worst
 * outcome of guessing wrong that way is the sentence reps already read.
 */
function usePasswordResetOffered(): boolean {
  const [offered, setOffered] = React.useState(false);
  React.useEffect(() => {
    const controller = new AbortController();
    api
      .publicGet<{ enabled: boolean }>("/auth/password-reset/enabled", controller.signal)
      .then(
        (answer) => setOffered(answer.enabled),
        () => undefined,
      );
    return () => controller.abort();
  }, []);
  return offered;
}

export function LoginPage() {
  const { user, login, loading } = useAuth();
  const location = useLocation();
  const [formError, setFormError] = React.useState<string | null>(null);
  const [reveal, setReveal] = React.useState(false);
  const resetOffered = usePasswordResetOffered();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: "", password: "" },
  });

  if (loading) return null;
  if (user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={postLoginPath(user.role, from)} replace />;
  }

  const onSubmit = form.handleSubmit(async (values) => {
    setFormError(null);
    try {
      await login(values.email, values.password);
    } catch (e) {
      // The server deliberately does not distinguish a wrong password from an unknown address
      // (it would enumerate the staff list), so neither does this.
      setFormError(
        e instanceof ApiError ? e.message : "Could not reach the server. Try again.",
      );
    }
  });

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-canvas px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-7 flex flex-col items-center text-center">
          <div className="mb-3 flex size-10 items-center justify-center rounded-lg bg-accent-9 text-white shadow-sm">
            <PhoneCall className="size-5" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold tracking-tight">AI Case Leads</h1>
          <p className="mt-1 text-sm text-text-muted">Sign in to the console</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-xl border border-border bg-surface p-5 shadow-sm"
          noValidate
        >
          {formError && (
            <div
              role="alert"
              className="flex items-start gap-2 rounded-md border border-danger-4 bg-danger-2 px-3 py-2 text-xs text-danger-11"
            >
              <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
              <span>{formError}</span>
            </div>
          )}

          <Field label="Email" htmlFor="email" error={form.formState.errors.email?.message}>
            <Input
              type="email"
              autoComplete="username"
              autoFocus
              placeholder="you@aicaseleads.com"
              invalid={Boolean(form.formState.errors.email)}
              {...form.register("email")}
            />
          </Field>

          <Field
            label="Password"
            htmlFor="password"
            error={form.formState.errors.password?.message}
          >
            <Input
              type={reveal ? "text" : "password"}
              autoComplete="current-password"
              invalid={Boolean(form.formState.errors.password)}
              trailing={
                <button
                  type="button"
                  onClick={() => setReveal((v) => !v)}
                  className="rounded p-1 text-text-subtle transition-colors hover:bg-surface-hover hover:text-text"
                  aria-label={reveal ? "Hide password" : "Show password"}
                  // Not in the tab order: it sits between the password field and Sign in, and
                  // tabbing into it every time is a small tax on the most repeated action here.
                  tabIndex={-1}
                >
                  {reveal ? <EyeOff /> : <Eye />}
                </button>
              }
              {...form.register("password")}
            />
          </Field>

          <Button
            type="submit"
            variant="default"
            size="lg"
            className="w-full"
            loading={form.formState.isSubmitting}
          >
            Sign in
          </Button>
        </form>

        <p className="mt-4 text-center text-xs text-text-subtle">
          {resetOffered ? (
            <Link to="/forgot-password" className="underline-offset-2 hover:underline">
              Forgotten your password?
            </Link>
          ) : (
            // A deployment with no SendGrid key and no SMTP relay cannot deliver a reset link, and
            // a link to a form that silently sends nothing is worse than the old sentence.
            "Forgotten your password? An admin can reset it for you."
          )}
        </p>
      </div>
    </div>
  );
}
